import { useState } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { classifyHref } from './lib/footerLinks'
import {
  useLandingContent,
  useLandingContentStore,
  DOWNLOAD_PLATFORM_LABELS,
  type AppDownload,
  type DownloadPlatform,
  type LandingContent,
} from '@/features/admin/content/landingContent'
import { usePvDesktopLatest } from './hooks/usePvDesktopLatest'
import { PV_DESKTOP_APP_ID, type PvDesktopLatest } from './lib/pvDesktopLatest'
import googlePlayBadge from '@/assets/badges/google-play-es.png'
import appStoreBadge from '@/assets/badges/app-store-es.svg'

/**
 * PÁGINA PÚBLICA `/descargas` — de dónde se bajan las aplicaciones del
 * ecosistema (goal `el-portal-lista-las-aplicaciones-para-descargar`).
 *
 * Tres cosas que no son obvias:
 *
 * 1. **El portal NO aloja los binarios.** La subida del CMS solo acepta
 *    imágenes de 8 MB y un `.apk` pesa diez veces eso: `apkUrl` y `windowsUrl`
 *    son URL que se pegan a mano en el editor (hoy apuntan a
 *    `repo.tecopos.com`). Aquí solo se enlazan.
 * 2. **Toda URL del CMS es entrada de usuario** y pasa por `classifyHref`
 *    antes de pintarse: solo sobrevive `http(s)` absoluto, así que un
 *    `javascript:` o un `//host` guardados en el documento no llegan al DOM.
 * 3. **La tarjeta solo enseña lo que existe.** Sin ningún enlace válido, la app
 *    se pinta como «Próximamente» en vez de con botones muertos — que es lo
 *    que pasa con Mensajero mientras no se publique.
 */

/** Filtro de la fila de chips; `todas` no filtra. */
type PlatformFilter = DownloadPlatform | 'todas'

const FILTROS: { key: PlatformFilter; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'android', label: DOWNLOAD_PLATFORM_LABELS.android },
  { key: 'ios', label: DOWNLOAD_PLATFORM_LABELS.ios },
  { key: 'windows', label: DOWNLOAD_PLATFORM_LABELS.windows },
  { key: 'tv', label: DOWNLOAD_PLATFORM_LABELS.tv },
]

/**
 * Destino externo ya validado, o `null`. Las tiendas y los binarios viven
 * siempre fuera del portal: una ruta interna aquí sería un error de quien
 * administra, no un enlace que valga la pena pintar.
 */
/**
 * Texto del CMS. Solo sobrevive una cadena: el documento es un JSON sin validar
 * y un `name: 42` guardado por accidente reventaría en el primer `.trim()`.
 */
function texto(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : ''
}

function externalHref(raw: unknown): string | null {
  const target = classifyHref(texto(raw) || null)
  return target && target.kind === 'external' ? target.href : null
}

/**
 * Origen de imagen aceptable para el ícono. El ícono NO es un enlace, así que
 * no vale `classifyHref` tal cual: el editor lo sube por `uploadImage`, que hoy
 * devuelve una URL del backend, y el documento puede traer además una imagen
 * incrustada de cuando se guardaban como `data:`. Se admiten esas dos formas —
 * `http(s)` y `data:image/…` — y nada más: cualquier otra cadena del CMS se
 * ignora y la tarjeta cae en el monograma, que es lo que se quiere.
 */
function imageSrc(raw: unknown): string | null {
  const value = texto(raw)
  if (!value) return null
  if (/^data:image\/[a-z0-9.+-]+[;,]/i.test(value)) return value
  // Una ruta propia (`/algo.png`) vale: es del portal, no del CMS ajeno.
  if (value.startsWith('/') && !value.startsWith('//')) return value
  return externalHref(value)
}

/**
 * 🚨 El documento del CMS NO se valida en ningún sitio: `fetchLandingContent`
 * hace un `JSON.parse(...) as LandingContent` y `withDefaults` es un spread
 * superficial —cubre «la clave no está», no «la clave trae basura»—. Y el `PUT`
 * del CMS todavía no lleva guard. Como el `AppErrorBoundary` envuelve la
 * aplicación ENTERA, un `apps: {}` o un `name: 42` guardados por accidente no
 * estropearían una tarjeta: tirarían la página pública entera para todo el
 * mundo hasta que alguien corrigiera el documento a mano.
 *
 * Por eso el documento **no se pinta nunca crudo**: se normaliza aquí, en el
 * borde de lectura, y de este punto hacia dentro los tipos ya son ciertos.
 */
interface AppSegura {
  id: string
  name: string
  tagline: string
  description: string
  icon: string | null
  platforms: DownloadPlatform[]
  play: string | null
  appStore: string | null
  apk: string | null
  apkVersion: string
  windows: string | null
  windowsVersion: string
}

function normalizar(app: AppDownload, indice: number): AppSegura {
  return {
    // El `id` solo alimenta la key de React y el color del monograma; si viene
    // roto, la posición sirve igual y no se pierde la tarjeta.
    id: texto(app.id) || `app-${indice}`,
    // Un `<h3>` vacío rompe la navegación por encabezados de un lector de
    // pantalla, así que siempre hay algo que leer.
    name: texto(app.name) || 'Aplicación',
    tagline: texto(app.tagline),
    description: texto(app.description),
    icon: imageSrc(app.icon),
    // 🚨 `p in DOWNLOAD_PLATFORM_LABELS` NO sirve: acepta `__proto__`,
    // `constructor` y `toString`, que están en la cadena de prototipos de
    // cualquier objeto. `DOWNLOAD_PLATFORM_LABELS['__proto__']` devolvería
    // `Object.prototype`, y React no sabe pintar eso: excepción y adiós página.
    platforms: Array.isArray(app.platforms)
      ? app.platforms.filter(
          (p): p is DownloadPlatform =>
            typeof p === 'string' && Object.hasOwn(DOWNLOAD_PLATFORM_LABELS, p),
        )
      : [],
    play: externalHref(app.playStoreUrl),
    appStore: externalHref(app.appStoreUrl),
    apk: externalHref(app.apkUrl),
    apkVersion: texto(app.apkVersion),
    windows: externalHref(app.windowsUrl),
    windowsVersion: texto(app.windowsVersion),
  }
}

/**
 * La tarjeta de TECOPOS escritorio se alimenta del `latest.yml` del canal (lo
 * lee `tecopos-portal-server`: `repo.tecopos.com` no deja al navegador). Si el
 * servidor no contesta, la tarjeta se queda con el `windowsUrl` del CMS, que es
 * el respaldo. El enlace del servidor pasa por el MISMO `externalHref` que los
 * del CMS: no se pinta nada que no sea `http(s)` absoluto.
 */
function conUltimaVersion(app: AppSegura, latest: PvDesktopLatest | null): AppSegura {
  if (!latest || app.id !== PV_DESKTOP_APP_ID) return app
  const windows = externalHref(latest.url)
  if (!windows) return app
  return { ...app, windows, windowsVersion: latest.version }
}

/** Las apps visibles del documento, ya normalizadas. */
function appsOf(
  downloads: LandingContent['downloads'] | undefined,
  latest: PvDesktopLatest | null = null,
): AppSegura[] {
  const apps = downloads?.apps
  if (!Array.isArray(apps)) return []
  return apps
    .filter((app) => app && typeof app === 'object' && app.visible === true)
    .map(normalizar)
    .map((app) => conUltimaVersion(app, latest))
}

/** Paleta del monograma de respaldo (cuando la app no tiene ícono subido). */
const MONOGRAMAS = [
  'from-[#e6702d] to-[#b8431a]',
  'from-[#1c1c1c] to-[#4a4a4a]',
  'from-[#2b6cb0] to-[#1a3d6b]',
  'from-[#5b4bd6] to-[#2f2580]',
  'from-[#0f8a7a] to-[#0a5a50]',
  'from-[#8a2be2] to-[#4b1a7a]',
  'from-[#d63a5e] to-[#8a1f3a]',
  'from-[#6b7280] to-[#374151]',
]

/** Color estable por app: la misma tarjeta no cambia de color entre visitas. */
function monogramaClase(id: string): string {
  let suma = 0
  for (let i = 0; i < id.length; i += 1) suma += id.charCodeAt(i)
  return MONOGRAMAS[suma % MONOGRAMAS.length]
}

/** Iniciales para el monograma: dos letras como mucho, sin puntuación. */
function iniciales(name: string): string {
  const palabras = name
    .replace(/[·.]/g, ' ')
    .split(/\s+/)
    .filter((p) => /[a-záéíóúñ]/i.test(p))
  return palabras
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function AppIcon({ app }: { app: AppSegura }) {
  if (app.icon) {
    return (
      <img
        src={app.icon}
        alt=""
        className="size-14 flex-none rounded-2xl object-cover"
      />
    )
  }
  return (
    <div
      aria-hidden="true"
      className={`grid size-14 flex-none place-items-center rounded-2xl bg-gradient-to-br ${monogramaClase(app.id)} text-sm font-extrabold tracking-tight text-white`}
    >
      {iniciales(app.name)}
    </div>
  )
}

/**
 * Insignia oficial de tienda. Las imágenes son las que publica cada tienda, en
 * español y sin retocar (ver `src/assets/badges/README.md`): no se recolorean
 * ni se les cambia la proporción.
 */
function StoreBadge({ href, src, alt }: { href: string; src: string; alt: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-11 items-center transition-transform hover:-translate-y-px"
    >
      <img src={src} alt={alt} className="h-11 w-auto" />
    </a>
  )
}

/** Botón propio del portal para la descarga directa (`.apk` / instalador). */
function DirectDownload({
  href,
  label,
  detail,
  dark,
}: {
  href: string
  label: string
  detail?: string
  dark?: boolean
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex h-11 flex-col justify-center rounded-[10px] border px-4 leading-tight transition-transform hover:-translate-y-px ${
        dark
          ? 'border-foreground bg-foreground text-white'
          : 'border-[#c4501a]/20 bg-[#fbeee5] text-[#c4501a]'
      }`}
    >
      <strong className="text-[13.5px] font-bold">{label}</strong>
      {detail ? (
        <span className={`text-[11.5px] tabular-nums ${dark ? 'text-white/70' : 'opacity-85'}`}>
          {detail}
        </span>
      ) : null}
    </a>
  )
}

function AppCard({ app }: { app: AppSegura }) {
  const { play, appStore, apk, windows } = app
  const sinEnlaces = !play && !appStore && !apk && !windows

  return (
    <article className="flex flex-col gap-4 rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3.5">
        <AppIcon app={app} />
        <div>
          <h3 className="text-[17px] font-bold tracking-tight">{app.name}</h3>
          {app.tagline ? (
            <p className="mt-0.5 text-[13.5px] text-muted-foreground">{app.tagline}</p>
          ) : null}
        </div>
      </div>

      {app.description ? <p className="text-sm">{app.description}</p> : null}

      {app.platforms.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {app.platforms.map((p) => (
            <li
              key={p}
              className="rounded-md border border-black/5 bg-neutral-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              {DOWNLOAD_PLATFORM_LABELS[p]}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-2.5 pt-1">
        {play ? (
          <StoreBadge href={play} src={googlePlayBadge} alt="Disponible en Google Play" />
        ) : null}
        {appStore ? (
          <StoreBadge href={appStore} src={appStoreBadge} alt="Consíguelo en el App Store" />
        ) : null}
        {apk ? (
          <DirectDownload
            href={apk}
            label="Descargar APK"
            detail={app.apkVersion ? `v${app.apkVersion}` : undefined}
          />
        ) : null}
        {windows ? (
          <DirectDownload
            href={windows}
            label="Descargar para Windows"
            detail={app.windowsVersion ? `v${app.windowsVersion}` : undefined}
            dark
          />
        ) : null}
        {sinEnlaces ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff4cc] px-2.5 py-1 text-xs font-bold text-[#8a6d00]">
            <i aria-hidden="true" className="size-[7px] rounded-full bg-current" />
            Próximamente
          </span>
        ) : null}
      </div>

      {windows ? (
        <p className="border-l-[3px] border-primary pl-2.5 text-[12.5px] text-muted-foreground">
          Windows puede mostrar un aviso de SmartScreen la primera vez: pulsa «Más información» y
          luego «Ejecutar de todas formas».
        </p>
      ) : null}
    </article>
  )
}

/** Los dos avisos que ahorran una llamada al soporte. */
function AntesDeInstalar({ hayApk, hayWindows }: { hayApk: boolean; hayWindows: boolean }) {
  if (!hayApk && !hayWindows) return null
  return (
    <section className="mx-auto max-w-7xl px-5 pb-16 lg:px-6">
      <span className="text-xs font-bold uppercase tracking-widest text-primary">
        Antes de instalar
      </span>
      <h2 className="mt-2.5 text-[26px] font-extrabold tracking-tight">
        Dos avisos que ahorran una llamada
      </h2>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {hayApk ? (
          <div className="rounded-[20px] border border-black/5 bg-neutral-50 p-6">
            <h3 className="mb-2 text-[15px] font-bold">Instalar un .apk en Android</h3>
            <ol className="grid list-decimal gap-1.5 pl-4 text-sm text-muted-foreground">
              <li>
                Descarga el archivo y ábrelo desde las notificaciones o desde{' '}
                <b className="font-semibold text-foreground">Archivos</b>.
              </li>
              <li>
                Si el teléfono pregunta, permite{' '}
                <b className="font-semibold text-foreground">
                  instalar aplicaciones de esta fuente
                </b>
                . Es un permiso de una sola vez.
              </li>
              <li>Al terminar, abre la app y entra con tu usuario de siempre.</li>
            </ol>
          </div>
        ) : null}
        {hayWindows ? (
          <div className="rounded-[20px] border border-black/5 bg-neutral-50 p-6">
            <h3 className="mb-2 text-[15px] font-bold">Instalar el punto de venta en Windows</h3>
            <ol className="grid list-decimal gap-1.5 pl-4 text-sm text-muted-foreground">
              <li>
                Ejecuta <b className="font-semibold text-foreground">TecoposPV-Setup</b>. Si
                aparece la pantalla azul de SmartScreen, pulsa{' '}
                <b className="font-semibold text-foreground">Más información</b> y después{' '}
                <b className="font-semibold text-foreground">Ejecutar de todas formas</b>.
              </li>
              <li>Al abrir por primera vez, vincula la caja con el servidor y las impresoras.</li>
              <li>
                Las versiones nuevas se descargan solas y se instalan cuando no hay un turno
                abierto.
              </li>
            </ol>
          </div>
        ) : null}
      </div>
    </section>
  )
}

/** Envoltorio común: la página siempre lleva el menú y el pie del portal. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground antialiased">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  )
}

export function DownloadsPage() {
  const { downloads, hidden } = useLandingContent()
  const status = useLandingContentStore((s) => s.status)
  const [filtro, setFiltro] = useState<PlatformFilter>('todas')
  const pvDesktopLatest = usePvDesktopLatest()

  if (status === 'loading') {
    return (
      <Shell>
        <div role="status" aria-busy="true" className="mx-auto max-w-7xl px-5 py-16 lg:px-6">
          <span className="sr-only">Cargando las aplicaciones…</span>
          <div className="h-10 w-2/3 animate-pulse rounded bg-neutral-100" />
          <div className="mt-4 h-4 w-5/6 animate-pulse rounded bg-neutral-100" />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-56 animate-pulse rounded-3xl bg-neutral-100" />
            ))}
          </div>
        </div>
      </Shell>
    )
  }

  if (status === 'error') {
    return (
      <Shell>
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-6">
          <h1 className="text-3xl font-bold tracking-tight">Aplicaciones</h1>
          <p className="mt-4 text-muted-foreground">
            No pudimos cargar la lista de aplicaciones. Revisa tu conexión e inténtalo de nuevo.
          </p>
          <button
            type="button"
            onClick={() => void useLandingContentStore.getState().load()}
            className="mt-6 inline-flex items-center rounded-xl bg-foreground px-5 py-3 text-sm font-semibold text-background"
          >
            Reintentar
          </button>
        </div>
      </Shell>
    )
  }

  // Apagada desde la administración: se dice, no se redirige en silencio ni se
  // finge un 404 — el menú y el pie tampoco la enlazan mientras esté así.
  if (hidden?.downloads) {
    return (
      <Shell>
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-6">
          <h1 className="text-3xl font-bold tracking-tight">Esta página no está disponible</h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            La sección de descargas está desactivada por ahora. Vuelve más tarde o escríbenos por
            los canales oficiales de TecoPOS.
          </p>
        </div>
      </Shell>
    )
  }

  const apps = appsOf(downloads, pvDesktopLatest)
  const visibles = filtro === 'todas' ? apps : apps.filter((app) => app.platforms.includes(filtro))
  const hayApk = visibles.some((app) => app.apk)
  const hayWindows = visibles.some((app) => app.windows)
  // El título y la entradilla también salen del documento sin validar: si
  // llegaran como objeto, React no sabría pintarlos y se llevaría la página.
  const titulo = texto(downloads?.title) || 'Aplicaciones para descargar'
  const entradilla = texto(downloads?.intro)

  return (
    <Shell>
      <section className="border-b border-black/5 bg-neutral-50 py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">
            Aplicaciones
          </span>
          <h1 className="mt-3 max-w-[16ch] text-[clamp(2rem,5vw,3.25rem)] font-extrabold leading-[1.05] tracking-tight">
            {titulo}
          </h1>
          {entradilla ? (
            <p className="mt-4 max-w-[58ch] text-[17px] text-muted-foreground">{entradilla}</p>
          ) : null}
          <div role="group" aria-label="Filtrar por plataforma" className="mt-7 flex flex-wrap gap-2">
            {FILTROS.map(({ key, label }) => {
              const activo = filtro === key
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => setFiltro(key)}
                  className={`rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    activo
                      ? 'border-foreground bg-foreground text-white'
                      : 'border-black/10 bg-white hover:border-foreground'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-6">
        {visibles.length === 0 ? (
          <p className="text-muted-foreground">
            {apps.length === 0
              ? 'Todavía no hay aplicaciones publicadas aquí.'
              : 'Ninguna aplicación para esa plataforma.'}
          </p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibles.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        )}
        <p className="mt-5 max-w-[70ch] text-[12.5px] text-muted-foreground">
          Las insignias de Google Play y del App Store son las oficiales que publica cada tienda.
          Los botones de descarga directa son propios del portal y los archivos se sirven desde el
          repositorio de TecoPOS.
        </p>
      </section>

      <AntesDeInstalar hayApk={hayApk} hayWindows={hayWindows} />
    </Shell>
  )
}
